import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import withStyles from "@mui/styles/withStyles";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import actions from "../../actions";
import ClientTable from "./ClientTable";
import styles from "./styles";
import { PageContainer, ANTableFilters } from "@answersnow/pkg-theme";
import { DISPLAY_ITEMS, FILTER_ITEMS } from "./constants";
import { getUserId } from "../../selectors";
import { LoadingTable } from "./LoadingTable";
import { sortFilterItems } from "./utils";
import useGetClinianClientsWithFilters from "hooks/api/clinician/useGetClinianClientsWithFilters";

const MyClientsContainer = ({ classes, history }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const userId = useSelector(getUserId);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("client");
  const [clientFilter, setClientFilter] = useState([]);
  const [insuranceCompanyFilter, setInsuranceCompanyFilter] = useState([]);
  const [clinicianFilter, setCliniciansFilter] = useState([]);
  const [supervisorFilter, setSupervisorFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  const setPageDetails = (payload) => dispatch(actions.setPageDetails(payload));

  const selectedSortItem = DISPLAY_ITEMS.find((item) => item.value === orderBy);

  const query = {
    page,
    size: rowsPerPage,
    sort: selectedSortItem?.sortField || "client.name",
    direction: "asc",
    ...(clientFilter.length > 0 && { clientIds: clientFilter.join(",") }),
    ...(insuranceCompanyFilter.length > 0 && { insuranceIds: insuranceCompanyFilter.join(",") }),
    ...(clinicianFilter.length > 0 && { clinicianIds: clinicianFilter.join(",") }),
    ...(supervisorFilter.length > 0 && { supervisorIds: supervisorFilter.join(",") }),
    ...(statusFilter.length > 0 && { statusIds: statusFilter.join(",") }),
  };

  const {
    clinicianClients: clients,
    clientsCountList,
    insuranceCountList,
    clinicianCountList,
    supervisorCountList,
    statusCountList,
    totalCount: count,
    clinicianClientsLoading: isClientsLoading,
  } = useGetClinianClientsWithFilters(userId, query);

  const hasActiveFilters =
    clientFilter.length > 0 ||
    insuranceCompanyFilter.length > 0 ||
    clinicianFilter.length > 0 ||
    supervisorFilter.length > 0 ||
    statusFilter.length > 0;

  useEffect(() => {
    setPageDetails({
      pageName: "Clients",
      currentPage: "clients",
      menu: "clinicianClients",
    });
  }, []);

  useEffect(() => {
    setPage(0);
  }, [
    clientFilter,
    insuranceCompanyFilter,
    clinicianFilter,
    supervisorFilter,
    statusFilter,
    orderBy,
  ]);

  const handleDetailsClick = (clientId) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    history.push(`/clients/${clientId}/dashboard`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value));
  };

  const handleClearFilters = () => {
    setClientFilter([]);
    setInsuranceCompanyFilter([]);
    setCliniciansFilter([]);
    setSupervisorFilter([]);
    setStatusFilter([]);
    setPage(0);
  };

  return (
    <PageContainer>
      {isClientsLoading || !userId ? (
        <LoadingTable />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "80vh",
          }}
        >
          <Box
            sx={{
              marginLeft: "-24px",
              marginRight: "-24px",
              paddingLeft: "24px",
              paddingRight: "24px",
              [theme.breakpoints.down("xl")]: {
                marginLeft: "-10px",
                marginRight: "-10px",
                paddingLeft: "10px",
                paddingRight: "10px",
              },
            }}
            className={classes.titleContainer}
          >
            <ANTableFilters
              orderingMenuItems={DISPLAY_ITEMS}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              filtersMenuItems={FILTER_ITEMS({
                clientsCountList: clientsCountList,
                clientFilter,
                setClientFilter,
                insuranceCountList: sortFilterItems(insuranceCountList || []),
                insuranceCompanyFilter,
                setInsuranceCompanyFilter,
                clinicianCountList: clinicianCountList,
                clinicianFilter,
                setCliniciansFilter,
                supervisorCountList: sortFilterItems(supervisorCountList || []),
                supervisorFilter,
                setSupervisorFilter,
                statusCountList: statusCountList,
                statusFilter,
                setStatusFilter,
              })}
              searchInput={false}
            />
          </Box>

          <ClientTable
            classes={classes}
            handleDetailsClick={handleDetailsClick}
            clients={clients}
            customersLoading={isClientsLoading}
            userId={userId}
            page={page}
            rowsPerPage={rowsPerPage}
            count={count ? parseInt(count) : 0}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleClearFilters={hasActiveFilters ? handleClearFilters : undefined}
          />
        </div>
      )}
    </PageContainer>
  );
};

export default withStyles(styles)(MyClientsContainer);
